import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { supabase, invokeEdgeFunction } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import type { User, Session } from '@supabase/supabase-js';
import type { 
  IArea,
  ITask,
  ITaskCompletion,
  IUserHousehold,
  IHousehold,
  IUserProfile,
  IHouseholdMemberInfo,
} from '../types';
import { z } from 'zod';
import { ZArea, ZTask, ZTaskCompletion } from '../types/zod-schemas';
// Circular import (useChatStore imports useStore): only accessed lazily inside
// actions via getState(), never at module init, so it is safe.
import { useChatStore } from './useChatStore';

interface IStoreState {
  // Auth state
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  userProfile: IUserProfile | null;

  // Current household context
  currentHouseholdId: string | null;

  // Data state
  households: IHousehold[];
  userHouseholds: IUserHousehold[];
  areas: IArea[];
  tasks: ITask[];
  taskCompletions: ITaskCompletion[];

  // Cleanliness data (task UUID → dirty percentage 0-100)
  cleanlinessMap: Record<string, number>;

  // Today's allocated tasks (task_completions with assigned_date = today)
  todayAllocations: ITaskCompletion[];

  // Members of the current household (loaded on demand)
  householdMembers: IHouseholdMemberInfo[];

  // Loading states
  loading: {
    auth: boolean;
    households: boolean;
    areas: boolean;
    tasks: boolean;
    completions: boolean;
  };
  
  // Real-time channels
  channels: RealtimeChannel[];
  realtimeRetryCount: number;

  // Actions
  setAuth: (user: User | null, session: Session | null) => void;
  setCurrentHousehold: (householdId: string | null) => void;
  /** User-initiated household switch: clears stale data and remembers the choice. */
  switchHousehold: (householdId: string) => void;
  
  // Data fetchers
  fetchUserProfile: () => Promise<void>;
  fetchUserHouseholds: () => Promise<void>;
  fetchHouseholdData: (householdId: string) => Promise<void>;
  fetchTasks: (householdId: string) => Promise<void>;
  fetchCleanliness: (householdId: string) => Promise<void>;
  fetchTodayAllocations: () => Promise<void>;
  fetchStreak: () => Promise<void>;
  fetchHouseholdMembers: (householdId: string) => Promise<IHouseholdMemberInfo[]>;
  markAllocationDone: (completionId: string, notes?: string) => Promise<ITaskCompletion | null>;

  // Mutations with optimistic updates
  createHousehold: (name: string) => Promise<IHousehold | null>;
  joinHousehold: (inviteCode: string, role?: 'child' | 'cleaner') => Promise<IUserHousehold | null>;
  createArea: (name: string) => Promise<IArea | null>;
  createTask: (task: Omit<ITaskInsert, 'household_uuid' | 'created_by_uuid'>) => Promise<ITask | null>;
  updateTask: (taskId: string, updates: Partial<ITaskUpdate>) => Promise<ITask | null>;
  deleteTask: (taskId: string) => Promise<void>;
  deleteArea: (areaId: string) => Promise<void>;
  deleteTaskCompletion: (completionId: string) => Promise<void>;
  updateTaskOrder: (taskId: string, sortOrder: number) => Promise<ITask | null>;
  completeTask: (taskId: string, notes?: string, completedAt?: string) => Promise<ITaskCompletion | null>;
  updateUserProfile: (updates: Partial<IUserUpdate>) => Promise<IUserProfile | null>;
  updateHouseholdSettings: (householdId: string, updates: Partial<IHousehold>) => Promise<IHousehold | null>;
  updateEffortBudget: (budget: Record<string, number>) => Promise<void>;
  assignTask: (taskId: string, userUuid: string | null) => Promise<ITask | null>;
  updateMemberRole: (membershipId: string, role: 'parent' | 'child' | 'cleaner') => Promise<void>;
  removeMember: (membershipId: string) => Promise<void>;
  leaveHousehold: (householdId: string) => Promise<boolean>;

  // Real-time subscriptions
  subscribeToRealtime: (householdId: string) => void;
  unsubscribeFromRealtime: () => void;
  
  // Utilities
  reset: () => void;
}

// For proper typing of task-related operations
import type { ITaskInsert, ITaskUpdate, IUserUpdate } from '../types';

/** localStorage key remembering the last household the user switched to. */
export const CURRENT_HOUSEHOLD_STORAGE_KEY = 'tidy:currentHouseholdId';

const readStoredHouseholdId = (): string | null => {
  try {
    return window.localStorage.getItem(CURRENT_HOUSEHOLD_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredHouseholdId = (householdId: string | null) => {
  try {
    if (householdId) {
      window.localStorage.setItem(CURRENT_HOUSEHOLD_STORAGE_KEY, householdId);
    } else {
      window.localStorage.removeItem(CURRENT_HOUSEHOLD_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode etc.) — switching still works in-memory.
  }
};

export const useStore = create<IStoreState>()(
  devtools(
    (set, get) => ({
  // Initial state
  user: null,
  session: null,
  isAuthenticated: false,
  userProfile: null,
  currentHouseholdId: null,
  households: [],
  userHouseholds: [],
  areas: [],
  tasks: [],
  taskCompletions: [],
  cleanlinessMap: {},
  todayAllocations: [],
  householdMembers: [],
  loading: {
    auth: true,
    households: false,
    areas: false,
    tasks: false,
    completions: false,
  },
  channels: [],
  realtimeRetryCount: 0,

  // Auth actions
  setAuth: (user, session) => {
    set({ 
      user, 
      session, 
      isAuthenticated: !!user,
      loading: { ...get().loading, auth: false }
    });
    
    if (user) {
      get().fetchUserProfile();
      get().fetchUserHouseholds();
    }
  },
  
  setCurrentHousehold: (householdId) => {
    const currentId = get().currentHouseholdId;
    
    if (currentId !== householdId) {
      // Unsubscribe from old household
      if (currentId) {
        get().unsubscribeFromRealtime();
      }
      
      set({ currentHouseholdId: householdId });
      
      // Subscribe to new household
      if (householdId) {
        get().fetchHouseholdData(householdId);
        get().subscribeToRealtime(householdId);
      }
    }
  },
  
  switchHousehold: (householdId) => {
    if (get().currentHouseholdId === householdId) return;
    writeStoredHouseholdId(householdId);
    // Clear data from the previous household so nothing stale flashes
    set({
      areas: [],
      tasks: [],
      taskCompletions: [],
      cleanlinessMap: {},
      todayAllocations: [],
      householdMembers: [],
    });
    get().setCurrentHousehold(householdId);

    // The AI chat conversation is per household: drop the old one so we never
    // show household A's messages/plan (or send A's conversation id) in B.
    const chat = useChatStore.getState();
    const { isOpen, chatMode } = chat;
    chat.reset();
    useChatStore.setState({ isOpen, chatMode });
    if (isOpen) {
      useChatStore.getState().loadConversation();
    }
  },

  // Data fetchers
  fetchUserProfile: async () => {
    const { user } = get();
    if (!user) return;
    
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('uuid', user.id)
      .single();
      
    if (!error && data) {
      // Check if we need to update the profile with Google data
      if (!data.profile_photo_url && user.user_metadata?.avatar_url) {
        const { data: updatedProfile } = await supabase
          .from('users')
          .update({
            profile_photo_url: user.user_metadata.avatar_url || user.user_metadata.picture || null,
            name: data.name || user.user_metadata.full_name || user.user_metadata.name || user.email?.split('@')[0] || 'User'
          })
          .eq('uuid', user.id)
          .select()
          .single();
          
        if (updatedProfile) {
          set({ userProfile: updatedProfile });
        } else {
          set({ userProfile: data });
        }
      } else {
        set({ userProfile: data });
      }
    } else if (error && error.code === 'PGRST116') {
      // No user profile found - create one
      // Create user profile with Google data directly
      const { data: newProfile, error: insertError } = await supabase
        .from('users')
        .insert({
          uuid: user.id,
          email: user.email!,
          name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'User',
          profile_photo_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || null
        })
        .select()
        .single();
        
      if (insertError) {
        console.error('Error creating user profile:', insertError);
        return;
      }
      
      if (newProfile) {
        set({ userProfile: newProfile });
      }
    }
  },
  
  fetchUserHouseholds: async () => {
    const { user } = get();
    if (!user) return;
    
    set({ loading: { ...get().loading, households: true } });
    
    const { data, error } = await supabase
      .from('user_households')
      .select(`
        *,
        household:households(*)
      `)
      .eq('user_uuid', user.id);
      
    if (!error && data) {
      set({ 
        userHouseholds: data,
        households: data.map(uh => uh.household).filter((h): h is IHousehold => h !== null),
        loading: { ...get().loading, households: false }
      });
      
      // Auto-select a household if none selected: prefer the one the user
      // last switched to (if they are still a member), else the first.
      if (data.length > 0 && !get().currentHouseholdId) {
        const storedId = readStoredHouseholdId();
        const preferred = data.find((uh) => uh.household_uuid === storedId);
        get().setCurrentHousehold((preferred ?? data[0]).household_uuid);
      }
    }
  },
  
  fetchHouseholdData: async (householdId: string) => {
    set({ loading: { ...get().loading, areas: true } });
    
    const { data: areas } = await supabase
      .from('areas')
      .select('*')
      .eq('household_uuid', householdId)
      .is('deleted_at', null);

    // Ignore results for a household the user has switched away from
    if (get().currentHouseholdId !== householdId) return;

    if (areas) {
      set({ 
        areas: areas || [],
        loading: { ...get().loading, areas: false }
      });
    }
    
    get().fetchTasks(householdId);
  },
  
  fetchTasks: async (householdId: string) => {
    set({ loading: { ...get().loading, tasks: true } });
    
    const { data: tasks } = await supabase
      .from('tasks')
      .select('*')
      .eq('household_uuid', householdId)
      .is('deleted_at', null)
      .order('sort_order', { ascending: true });

    if (get().currentHouseholdId !== householdId) return;

    if (tasks) {
      set({ 
        tasks: tasks || [],
        loading: { ...get().loading, tasks: false }
      });
      
      // Also fetch task completions
      const { data: completions } = await supabase
        .from('task_completions')
        .select('*')
        .eq('household_uuid', householdId)
        .is('deleted_at', null);

      if (get().currentHouseholdId !== householdId) return;

      if (completions) {
        set({ taskCompletions: completions });
      }
    }

    // Fetch cleanliness data, today's allocations, and streak after tasks are loaded
    get().fetchCleanliness(householdId);
    get().fetchTodayAllocations();
    get().fetchStreak();
  },

  fetchCleanliness: async (householdId: string) => {
    try {
      const response = await invokeEdgeFunction('cleanliness', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ household_uuid: householdId }),
      });

      if (!response.ok) return;

      const result = await response.json();
      if (get().currentHouseholdId !== householdId) return;
      if (result.tasks && Array.isArray(result.tasks)) {
        const map: Record<string, number> = {};
        for (const item of result.tasks) {
          map[item.task_uuid] = item.cleanliness_percentage;
        }
        set({ cleanlinessMap: map });
      }
    } catch (error) {
      console.error('Error fetching cleanliness:', error);
    }
  },

  fetchTodayAllocations: async () => {
    const { user, currentHouseholdId } = get();
    if (!user || !currentHouseholdId) return;

    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('task_completions')
      .select('*')
      .eq('household_uuid', currentHouseholdId)
      .eq('assigned_to_user_uuid', user.id)
      .eq('assigned_date', today)
      .is('deleted_at', null);

    if (get().currentHouseholdId !== currentHouseholdId) return;

    if (!error && data) {
      set({ todayAllocations: data });
    }
  },

  fetchStreak: async () => {
    const { user, currentHouseholdId } = get();
    if (!user || !currentHouseholdId) return;

    try {
      const response = await invokeEdgeFunction('streaks', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_uuid: user.id,
          household_uuid: currentHouseholdId,
        }),
      });

      if (!response.ok) return;

      const result = await response.json();

      // Update the local user_household with streak data
      set({
        userHouseholds: get().userHouseholds.map((uh) =>
          uh.household_uuid === currentHouseholdId && uh.user_uuid === user.id
            ? {
                ...uh,
                current_streak: result.current_streak ?? 0,
                longest_streak: result.longest_streak ?? 0,
              }
            : uh
        ),
      });
    } catch (error) {
      console.error('Error fetching streak:', error);
    }
  },

  fetchHouseholdMembers: async (householdId: string) => {
    const { data, error } = await supabase
      .from('user_households')
      .select('uuid, user_uuid, role, users:user_uuid(uuid, name, profile_photo_url)')
      .eq('household_uuid', householdId)
      .order('joined_at', { ascending: true });

    if (error || !data) {
      if (error) console.error('Error fetching household members:', error);
      return get().householdMembers;
    }

    const members: IHouseholdMemberInfo[] = data
      .filter((m): m is typeof m & { users: { uuid: string; name: string; profile_photo_url: string | null } } =>
        m.users !== null && typeof m.users === 'object'
      )
      .map((m) => ({
        membership_uuid: m.uuid,
        user_uuid: m.users.uuid,
        name: m.users.name,
        profile_photo_url: m.users.profile_photo_url,
        role: m.role,
      }));

    // Only keep the result if the user hasn't switched households meanwhile
    if (get().currentHouseholdId === householdId) {
      set({ householdMembers: members });
    }
    return members;
  },

  markAllocationDone: async (completionId: string, notes?: string) => {
    const { user } = get();
    if (!user) throw new Error('No user');

    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('task_completions')
      .update({
        completed_at: now,
        completed_by_user_uuid: user.id,
        notes: notes ?? null,
      })
      .eq('uuid', completionId)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned');

    // Update local state
    set({
      todayAllocations: get().todayAllocations.map(a =>
        a.uuid === completionId ? { ...a, completed_at: now, notes: notes ?? null } : a
      ),
      taskCompletions: get().taskCompletions.map(tc =>
        tc.uuid === completionId ? { ...tc, completed_at: now, notes: notes ?? null } : tc
      ),
    });

    return data;
  },

  // Mutations
  createHousehold: async (name: string) => {
    const { user } = get();
    if (!user) {
      console.error('No user found when creating household');
      return null;
    }
    

    // Check current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }
    
    // First, insert (no returning) so we don't depend on row visibility
    const { error: insertError } = await supabase
      .from('households')
      .insert({ name, created_by_uuid: user.id });

    if (insertError) {
      console.error('Error creating household (insert):', insertError);
      throw insertError;
    }

    // Then, fetch the most recently created household you can see (trigger has added membership)
    const { data, error } = await supabase
      .from('households')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
      
    if (error) {
      console.error('Error creating household:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }
      
    if (!data) {
      throw new Error('No data returned from household creation');
    }
      
    get().fetchUserHouseholds();
    return data;
  },
  
  joinHousehold: async (inviteCode: string, role: 'child' | 'cleaner' = 'child') => {
    const { user } = get();
    if (!user) throw new Error('No user');

    const { data: household, error: lookupError } = await supabase
      .from('households')
      .select('uuid')
      .eq('invite_code', inviteCode)
      .single();

    if (lookupError) throw new Error('Invalid invite code');
    if (!household) throw new Error('Household not found');

    const { data, error } = await supabase
      .from('user_households')
      .insert({
        user_uuid: user.id,
        household_uuid: household.uuid,
        role,
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Failed to join household');

    get().fetchUserHouseholds();
    return data;
  },
  
  createArea: async (name: string) => {
    const { currentHouseholdId } = get();
    if (!currentHouseholdId) throw new Error('No household selected');

    const { data, error } = await supabase
      .from('areas')
      .insert({ name, household_uuid: currentHouseholdId })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned from area creation');

    set({ areas: [...get().areas, data] });
    return data;
  },
  
  createTask: async (task: Omit<ITaskInsert, 'household_uuid' | 'created_by_uuid'>) => {
    const { currentHouseholdId, user } = get();
    if (!currentHouseholdId || !user) throw new Error('No household or user');

    const { data, error } = await supabase
      .from('tasks')
      .insert({
        ...task,
        household_uuid: currentHouseholdId,
        created_by_uuid: user.id
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned from task creation');

    set({ tasks: [...get().tasks, data] });
    return data;
  },
  
  updateTask: async (taskId: string, updates: Partial<ITaskUpdate>) => {
    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('uuid', taskId)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned from task update');

    set({
      tasks: get().tasks.map(t =>
        t.uuid === taskId ? { ...t, ...data } : t
      )
    });
    return data;
  },
  
  deleteTask: async (taskId: string) => {
    const { user } = get();
    if (!user) throw new Error('No user found');

    const { error } = await supabase
      .from('tasks')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user.id
      })
      .eq('uuid', taskId);

    if (error) throw error;

    set({
      tasks: get().tasks.filter(t => t.uuid !== taskId)
    });
  },
  
  deleteArea: async (areaId: string) => {
    const { user } = get();
    if (!user) throw new Error('No user found');

    // First, soft delete all tasks in this area
    const areaTasks = get().tasks.filter(t => t.area_uuid === areaId);
    const taskDeleteResults = await Promise.all(
      areaTasks.map(task =>
        supabase
          .from('tasks')
          .update({
            deleted_at: new Date().toISOString(),
            deleted_by: user.id
          })
          .eq('uuid', task.uuid)
      )
    );

    const taskError = taskDeleteResults.find(r => r.error);
    if (taskError?.error) throw taskError.error;

    // Then soft delete the area
    const { error } = await supabase
      .from('areas')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user.id
      })
      .eq('uuid', areaId);

    if (error) throw error;

    set({
      areas: get().areas.filter(a => a.uuid !== areaId),
      tasks: get().tasks.filter(t => t.area_uuid !== areaId)
    });
  },
  
  deleteTaskCompletion: async (completionId: string) => {
    const { user } = get();
    if (!user) throw new Error('No user found');

    const { error } = await supabase
      .from('task_completions')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user.id
      })
      .eq('uuid', completionId);

    if (error) throw error;

    set({
      taskCompletions: get().taskCompletions.filter(tc => tc.uuid !== completionId)
    });
  },
  
  updateTaskOrder: async (taskId: string, sortOrder: number) => {
    const { data, error } = await supabase
      .from('tasks')
      .update({ sort_order: sortOrder })
      .eq('uuid', taskId)
      .select()
      .single();
      
    if (!error && data) {
      set({ 
        tasks: get().tasks.map(t => 
          t.uuid === taskId ? { ...t, sort_order: sortOrder } : t
        )
      });
      return data;
    }
    
    return null;
  },
  
  completeTask: async (taskId: string, notes?: string, completedAt?: string) => {
    const { user, currentHouseholdId } = get();
    if (!user || !currentHouseholdId) throw new Error('No user or household');

    const task = get().tasks.find(t => t.uuid === taskId);
    if (!task) throw new Error('Task not found');

    const { data, error } = await supabase
      .from('task_completions')
      .insert({
        task_uuid: taskId,
        completed_by_user_uuid: user.id,
        completed_at: completedAt || new Date().toISOString(),
        points_earned: task.points,
        household_uuid: currentHouseholdId,
        notes
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned from task completion');

    set({
      taskCompletions: [...get().taskCompletions, data],
      tasks: get().tasks.map(t =>
        t.uuid === taskId
          ? { ...t, last_completed_at: data.completed_at }
          : t
      )
    });
    return data;
  },
  
  updateUserProfile: async (updates: Partial<IUserUpdate>) => {
    const { user } = get();
    if (!user) return null;
    
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('uuid', user.id)
      .select()
      .single();
      
    if (!error && data) {
      set({ userProfile: data });
      return data;
    }
    
    return null;
  },
  
  updateHouseholdSettings: async (householdId: string, updates: Partial<IHousehold>) => {
    const { data, error } = await supabase
      .from('households')
      .update(updates)
      .eq('uuid', householdId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating household settings:', error);
      return null;
    }
    
    if (data) {
      // Update the local state
      set(state => ({
        households: state.households.map(h => 
          h.uuid === householdId ? data : h
        )
      }));
      
      return data;
    }
    
    return null;
  },

  updateEffortBudget: async (budget: Record<string, number>) => {
    const { user, currentHouseholdId } = get();
    if (!user || !currentHouseholdId) return;

    // Find the user_household record
    const uh = get().userHouseholds.find(
      (x) => x.household_uuid === currentHouseholdId && x.user_uuid === user.id
    );
    if (!uh) return;

    const { error } = await supabase
      .from('user_households')
      .update({ effort_budget: budget })
      .eq('uuid', uh.uuid);

    if (!error) {
      set({
        userHouseholds: get().userHouseholds.map((x) =>
          x.uuid === uh.uuid ? { ...x, effort_budget: budget } : x
        ),
      });
    }
  },

  assignTask: async (taskId: string, userUuid: string | null) => {
    const { data, error } = await supabase
      .from('tasks')
      .update({ default_assigned_to_user_uuid: userUuid })
      .eq('uuid', taskId)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned from task assignment');

    set({
      tasks: get().tasks.map(t =>
        t.uuid === taskId ? { ...t, default_assigned_to_user_uuid: userUuid } : t
      ),
    });
    return data;
  },

  updateMemberRole: async (membershipId: string, role: 'parent' | 'child' | 'cleaner') => {
    const response = await invokeEdgeFunction(`members/${membershipId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      const body = await response.json();
      throw new Error(body.error || 'Failed to update role');
    }

    set({
      userHouseholds: get().userHouseholds.map(uh =>
        uh.uuid === membershipId ? { ...uh, role } : uh
      ),
      householdMembers: get().householdMembers.map(m =>
        m.membership_uuid === membershipId ? { ...m, role } : m
      ),
    });
  },

  removeMember: async (membershipId: string) => {
    const response = await invokeEdgeFunction(`members/${membershipId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const body = await response.json();
      throw new Error(body.error || 'Failed to remove member');
    }

    set({
      userHouseholds: get().userHouseholds.filter(uh => uh.uuid !== membershipId),
      householdMembers: get().householdMembers.filter(m => m.membership_uuid !== membershipId),
    });
  },

  leaveHousehold: async (householdId: string) => {
    const response = await invokeEdgeFunction('members/leave', {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ household_uuid: householdId }),
    });

    if (!response.ok) {
      const body = await response.json();
      throw new Error(body.error || 'Failed to leave household');
    }

    // Remove from local state
    const remaining = get().userHouseholds.filter(uh => uh.household_uuid !== householdId);
    set({
      userHouseholds: remaining,
      households: get().households.filter(h => h.uuid !== householdId),
    });

    // Switch to next available household or null
    if (get().currentHouseholdId === householdId) {
      const nextHousehold = remaining.length > 0 ? remaining[0].household_uuid : null;
      get().setCurrentHousehold(nextHousehold);
    }

    return remaining.length > 0;
  },

  // Real-time subscriptions
  subscribeToRealtime: (householdId: string) => {
    const channel = supabase
      .channel(`household-${householdId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          filter: `household_uuid=eq.${householdId}`
        },
        (payload) => {
          const { eventType, table, new: newRecord, old: oldRecord } = payload;
          
          switch (table) {
            case 'areas':
              if (eventType === 'INSERT' && newRecord) {
                try {
                  const area = ZArea.parse(newRecord);
                  set({ areas: [...get().areas, area] });
                } catch (error) {
                  console.error('Failed to parse area INSERT:', error);
                }
              } else if (eventType === 'UPDATE' && newRecord) {
                try {
                  const area = ZArea.parse(newRecord);
                  // Check if it's a soft delete
                  if (area.deleted_at) {
                    set({
                      areas: get().areas.filter(a => a.uuid !== area.uuid)
                    });
                  } else {
                    set({
                      areas: get().areas.map(a => 
                        a.uuid === area.uuid ? area : a
                      )
                    });
                  }
                } catch (error) {
                  console.error('Failed to parse area UPDATE:', error);
                }
              } else if (eventType === 'DELETE') {
                try {
                  // For DELETE events, we need to get the UUID from oldRecord
                  const { uuid } = z.object({ uuid: z.string() }).parse(oldRecord);
                  set({
                    areas: get().areas.filter(a => a.uuid !== uuid)
                  });
                } catch (error) {
                  console.error('Failed to parse area DELETE:', error);
                }
              }
              break;
              
            case 'tasks':
              if (eventType === 'INSERT' && newRecord) {
                try {
                  const task = ZTask.parse(newRecord);
                  set({ tasks: [...get().tasks, task] });
                } catch (error) {
                  console.error('Failed to parse task INSERT:', error);
                }
              } else if (eventType === 'UPDATE' && newRecord) {
                try {
                  const task = ZTask.parse(newRecord);
                  // Check if it's a soft delete
                  if (task.deleted_at) {
                    set({
                      tasks: get().tasks.filter(t => t.uuid !== task.uuid)
                    });
                  } else {
                    set({
                      tasks: get().tasks.map(t => 
                        t.uuid === task.uuid ? task : t
                      )
                    });
                  }
                } catch (error) {
                  console.error('Failed to parse task UPDATE:', error);
                }
              } else if (eventType === 'DELETE') {
                try {
                  // For DELETE events, we need to get the UUID from oldRecord
                  const { uuid } = z.object({ uuid: z.string() }).parse(oldRecord);
                  set({
                    tasks: get().tasks.filter(t => t.uuid !== uuid)
                  });
                } catch (error) {
                  console.error('Failed to parse task DELETE:', error);
                }
              }
              break;
              
            case 'task_completions':
              if (eventType === 'INSERT' && newRecord) {
                try {
                  const completion = ZTaskCompletion.parse(newRecord);
                  set({ taskCompletions: [...get().taskCompletions, completion] });
                } catch (error) {
                  console.error('Failed to parse task completion INSERT:', error);
                }
              } else if (eventType === 'UPDATE' && newRecord) {
                try {
                  const completion = ZTaskCompletion.parse(newRecord);
                  if (completion.deleted_at) {
                    set({
                      taskCompletions: get().taskCompletions.filter(tc => tc.uuid !== completion.uuid)
                    });
                  }
                } catch (error) {
                  console.error('Failed to parse task completion UPDATE:', error);
                }
              }
              break;
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          set({ realtimeRetryCount: 0 });
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          const retryCount = get().realtimeRetryCount;
          if (retryCount < 5) {
            const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
            console.warn(`Realtime ${status}, reconnecting in ${delay}ms (attempt ${retryCount + 1}/5)`);
            set({ realtimeRetryCount: retryCount + 1 });
            setTimeout(() => {
              get().unsubscribeFromRealtime();
              get().subscribeToRealtime(householdId);
            }, delay);
          } else {
            console.error('Realtime subscription failed after 5 retries');
          }
        }
      });

    set({ channels: [...get().channels, channel] });
  },
  
  unsubscribeFromRealtime: () => {
    const { channels } = get();
    channels.forEach(channel => {
      supabase.removeChannel(channel);
    });
    set({ channels: [] });
  },
  
  // Reset store
  reset: () => {
    get().unsubscribeFromRealtime();
    set({
      user: null,
      session: null,
      isAuthenticated: false,
      userProfile: null,
      currentHouseholdId: null,
      households: [],
      userHouseholds: [],
      areas: [],
      tasks: [],
      taskCompletions: [],
      cleanlinessMap: {},
      todayAllocations: [],
      householdMembers: [],
    });
  },
    }),
    {
      name: 'tidy-store',
    }
  )
);